import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import "./NavbarPanel.css";
import Logo from "../Logo";

/**
 * Custom React hook to fetch and return the current user's name.
 *
 * How it works:
 * - Reads the JWT token from sessionStorage.
 * - Sends a GET request to the backend /api/profile endpoint with the token in the Authorization header.
 * - Updates the userName state with the returned name from the backend.
 *
 * Parameters: None.
 * Return type: String (the user's name, or "" if not loaded).
 * Usage: Call useUserName() inside a component to get the current user's name.
 */
function useUserName() {
  const [userName, setUserName] = useState("");
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    async function fetchUserProfile() {
      try {
        const res = await fetch("http://localhost:5030/api/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch user profile");
        const data = await res.json();
        setUserName(data.name);
      } catch (err) {
        console.error("Error fetching user profile:", err);
      }
    }
    fetchUserProfile();
  }, []);
  return userName;
}

/**
 * Renders the navigation links for the panel.
 *
 * Parameters:
 * - navigate: Function from react-router-dom's useNavigate hook, used to change routes.
 *
 * Return type: JSX.Element (a div containing clickable navigation links).
 * Usage: Used inside NavbarPanel to display navigation options.
 */
function NavbarLinks({ navigate }) {
  return (
    <div className="navbar-links">
      <span onClick={() => navigate("/panel")}>Inquires</span>
      <span onClick={() => navigate("/Application")}>Application</span>
      <span onClick={() => navigate("/Employees")}>Employees</span>
      <span onClick={() => navigate("/Settings")}>Settings</span>
    </div>
  );
}

/**
 * Main NavbarPanel component.
 *
 * How it works:
 * - Uses useNavigate to get the navigation function.
 * - Uses useUserName to fetch and display the current user's name.
 * - Renders the logo, navigation links, and user info.
 * - Provides a sign out button that removes the token from sessionStorage and redirects to login.
 *
 * Parameters: None.
 * Return type: JSX.Element (the complete navigation bar for the panel).
 * Usage: Used as the navigation bar for authenticated panel routes.
 */
function NavbarPanel() {
  const navigate = useNavigate();
  const userName = useUserName();

  function handleSignOut() {
    sessionStorage.removeItem("token");
    navigate("/login");
  }

  return (
    <nav className="my-navbar navbar-panel">
      <div className="navbar-logo">
        <Logo />
      </div>
        <NavbarLinks navigate={navigate} />
      <div className="navbar-user">
        <span>{userName || "Loading..."}</span>
        <button onClick={handleSignOut}>Sign Out</button>
      </div>
    </nav>
  );
}

export default NavbarPanel;
